import {
  CatalogChoice,
  CatalogClientScript,
  CatalogMetadata,
  CatalogPrimitiveValue,
  CatalogVariable,
  NullableFriendlyType,
  ReferenceOption,
  RenderFieldType,
  RenderSection,
  RenderVariable,
} from '../../../models/catalog-form';

// Conditional policies, `setValue` / `setMandatory` inside branching JS,
// onChange scripts, and every runtime that reads `g_form` state are still
// out of scope and require the SDK — see the deferred list in
// docs/INEXP-2496-Plan.md.
export interface CatalogStaticOverride {
  visible?: boolean;
  readOnly?: boolean;
  mandatory?: boolean;
}

// Temporary gate: SNOW pairs readOnly UI policies with `gs.getUserID()`-style
// defaults that resolve to the logged-in user server-side (e.g. `hlr_requestedBy`
// is readonly + auto-filled). Our renderer parses those expressions but has no
// user-resolution path yet, so honouring the readOnly override alone would leave
// the user staring at an empty greyed-out control. Skip the readOnly patch for
// now; keep visibility / mandatory. Flip this to `true` when Prashant's SDK
// wires up the value-resolution pipeline.
const APPLY_UI_POLICY_READ_ONLY = false;

const SET_DISPLAY_HIDE = /g_form\s*\.\s*setDisplay\s*\(\s*['"]([^'"]+)['"]\s*,\s*false\s*\)/g;

function _triState(value: unknown): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

function _buildUnconditionalPatch(action: {
  visible?: string;
  readOnly?: string;
  mandatory?: string;
}): CatalogStaticOverride {
  const patch: CatalogStaticOverride = {};
  const visible = _triState(action.visible);
  if (visible !== undefined) patch.visible = visible;
  if (APPLY_UI_POLICY_READ_ONLY) {
    const readOnly = _triState(action.readOnly);
    if (readOnly !== undefined) patch.readOnly = readOnly;
  }
  const mandatory = _triState(action.mandatory);
  if (mandatory !== undefined) patch.mandatory = mandatory;
  return patch;
}

// Only the visible dimension is safe to reverse-default; readOnly / mandatory
// reversals could produce editable-when-should-be-required states that the
// user could submit past. Leave those to the runtime evaluator when it lands.
function _buildReverseVisiblePatch(action: {
  visible?: string;
}): CatalogStaticOverride {
  const inverted = _triState(action.visible);
  return inverted === undefined ? {} : { visible: !inverted };
}

type OverrideMerge = (name: string, patch: CatalogStaticOverride) => void;

// `reverseIfFalse: true` + a non-empty condition means "apply the action when
// the condition matches; otherwise apply its reverse". Without a runtime
// evaluator we cannot check the condition, but the initial state (nothing
// selected yet) is always "condition false" → the reversed action applies.
// For `visible: 'true'` that reverses to hidden, which matches SNOW's initial
// render for cascading-visibility fields (AWS/Azure/GCP under the Galileo
// service picker in the SNOW portal).
function _applyPolicyToOverrides(
  policy: CatalogMetadata['uiPolicies'][number],
  merge: OverrideMerge,
): void {
  if (policy.onLoad !== true) return;
  const unconditional = (policy.conditions ?? '') === '';
  const reverseDefault = policy.reverseIfFalse === true && !unconditional;
  if (!unconditional && !reverseDefault) return;
  for (const action of policy.actions ?? []) {
    const patch = unconditional
      ? _buildUnconditionalPatch(action)
      : _buildReverseVisiblePatch(action);
    merge(action.variableName, patch);
  }
}

function _applyClientScriptHides(
  scripts: CatalogMetadata['clientScripts'],
  merge: OverrideMerge,
): void {
  for (const script of scripts ?? []) {
    if (script.type !== 'onLoad') continue;
    for (const match of script.script.matchAll(SET_DISPLAY_HIDE)) {
      merge(match[1], { visible: false });
    }
  }
}

export function collectStaticOverrides(
  metadata: CatalogMetadata | null
): Map<string, CatalogStaticOverride> {
  const overrides = new Map<string, CatalogStaticOverride>();
  if (!metadata) return overrides;
  const merge: OverrideMerge = (name, patch) => {
    if (Object.keys(patch).length === 0) return;
    overrides.set(name, { ...overrides.get(name), ...patch });
  };
  for (const policy of metadata.uiPolicies ?? []) {
    _applyPolicyToOverrides(policy, merge);
  }
  _applyClientScriptHides(metadata.clientScripts, merge);
  return overrides;
}

// SN emits the raw variable name as `label` for fields it never intended to
// render (system carriers, request-type flags). When one side has a
// human-readable label and the other has the raw name, prefer the human one —
// otherwise the form shows `gitlab_product_access` instead of `Product access`.
export function pickLabel(
  metaLabel: string | undefined,
  itemLabel: string | undefined,
  name: string
): string {
  // Some SN metadata bakes the required-marker asterisk into the label itself
  // ("Requested for *"). Our template renders its own aria-hidden asterisk
  // when `mandatory` is true, so a raw trailing `*` doubles up ("Requested
  // for **"). Strip trailing asterisks and whitespace before picking.
  const clean = (s: string | undefined) =>
    s == null ? undefined : s.trim().replace(/\*+$/, '').trim() || undefined;
  const m = clean(metaLabel);
  const i = clean(itemLabel);
  if (m && m !== name) return m;
  if (i && i !== name) return i;
  return m ?? i ?? name;
}

// Preserves the API-returned order (critical rule #1). Flattens variable-set
// wrappers whose `order === -1` (critical rule #2) so their children render in
// place as if they were top-level fields — no fixture coverage this pass, but
// the code path stays so future slices don't need to reintroduce it.
//
// Shape mismatch reconciled here: the item response uses `hidden: boolean`
// (a field is displayed when `hidden === false`), while the metadata response
// uses `visible: boolean`. We invert `hidden` on the item side, then let
// metadata's `visible` override (critical rule #5). `mandatory` is additive.
//
// `readOnly` deliberately does NOT follow rule #5 (INEXP-2383): the metadata
// reports `readOnly: true` for variables SN renders editable (e.g.
// `hlr_acknowledgement`, `hlr_devices` on a99afbe4… — RoSE shows both
// editable, and the item response says `readOnly: false`). The item response
// matches SN, so it is the source; UI policies / client scripts still apply
// on top. See deviations/INEXP-2383-catalog-form-no-feature-flag.md.
//
// SNOW's own portal never renders leaf catalog variables with `order < 0`
// (see the browserstack license item: `gitlab_request_type` / `trigger` are
// system carriers whose value is set by the item definition). We match that
// by forcing `visible = false` for any non-wrapper with a negative order —
// variable-set wrappers with `order === -1` were already flattened away.
//
// Finally, `collectStaticOverrides(metadata)` folds in the visibility /
// readonly / mandatory flags SNOW itself would apply from unconditional
// onLoad UI policies and literal `setDisplay(name, false)` client scripts.
function _mergeFlags(
  v: CatalogVariable,
  meta: CatalogMetadata['variables'][number] | undefined,
  override: CatalogStaticOverride | undefined,
): { readOnly: boolean; visible: boolean; mandatory: boolean } {
  const isSystemCarrier = typeof v.order === 'number' && v.order < 0;
  const readOnlyFromMerge = v.readOnly === true;
  const itemVisible = v.hidden !== true;
  const visibleFromMerge =
    typeof meta?.visible === 'boolean' ? meta.visible : itemVisible;
  const mandatoryFromMerge = v.mandatory === true || meta?.mandatory === true;
  return {
    readOnly: override?.readOnly ?? readOnlyFromMerge,
    visible: isSystemCarrier ? false : override?.visible ?? visibleFromMerge,
    mandatory: override?.mandatory ?? mandatoryFromMerge,
  };
}

type VarMeta = CatalogMetadata['variables'][number];

// Item ships `attributes` as a comma-separated `key=value` string; metadata
// ships them pre-parsed as an object. Merge: parse the item side, then let
// metadata keys override (metadata is authoritative).
function _mergeAttributes(v: CatalogVariable, meta: VarMeta | undefined): Record<string, string> {
  return { ...parseAttributes(v.attributes ?? ''), ...meta?.attributes };
}

// SN's metadata endpoint carries reference wiring under different keys
// (`reference` / `referenceQual`) than the item endpoint (`referenceTable`
// / `refQualifier`). For glide_list / list_collector fields the item side
// omits both, so fall back to metadata when item is empty.
// SN's watch_list ships as list_collector with no explicit referenceTable
// (the sys_user target is implicit — `glide_list=true` in attributes is the
// only wire signal). Default to sys_user so the chip picker gets a working
// typeahead; any explicit table on item or metadata still wins.
// r2f 1.1.3: list collectors carry their source table in metadata `listTable`.
function _resolveReferenceTable(v: CatalogVariable, meta: VarMeta | undefined, renderType: string): string | null {
  if (renderType === 'reference_multi') return v.referenceTable ?? meta?.listTable ?? meta?.reference ?? 'sys_user';
  return v.referenceTable ?? meta?.reference ?? null;
}

// Plain encoded query or `javascript:` expression. Item `refQualifier` wins;
// metadata ships the raw qualifier as `referenceQual`, or as
// `referenceQualCondition` for `simple` qualifiers.
function _resolveRefQualifier(v: CatalogVariable, meta: VarMeta | undefined): string | null {
  return v.refQualifier || meta?.referenceQual || meta?.referenceQualCondition || null;
}

// First usable value wins: the item's server-resolved `value`, then metadata
// `defaultValueEvaluated` (r2f 1.1.3, user-scoped via `?user=`), then the raw
// definitions. The item ships `defaultValue: ""` alongside `value` (Galileo
// `type_of_access`: "" + "Full Access"), so `??` alone would keep the empty
// string and leave every `type_of_access=…` UI policy false. Unevaluated
// `javascript:` defaults are never usable as a value.
function _isUsableValue(value: CatalogPrimitiveValue | undefined): value is Exclude<CatalogPrimitiveValue, null> {
  if (value == null || value === '') return false;
  return !(typeof value === 'string' && value.trim().startsWith('javascript:'));
}

function _resolveDefaultValue(v: CatalogVariable, meta: VarMeta | undefined): CatalogPrimitiveValue {
  const candidates = [v.value, meta?.defaultValueEvaluated, v.defaultValue, meta?.defaultValue];
  const usable = candidates.find(_isUsableValue);
  if (usable !== undefined) return usable;
  // Keep an explicit `false` (checkboxes) / empty string rather than null.
  return v.value ?? v.defaultValue ?? null;
}

// Mulesoft evaluates `javascript:` reference defaults server-side and ships
// the result as `value` (sys_id) + `displayValue`, plus `userName` / `email`
// for sys_user. Keyed `user_name` / `email` so the seeded option matches what
// a typeahead record carries. A `javascript:` value means the backend did not
// evaluate it — leave the field empty rather than seed a script as a sys_id.
function _resolveReferenceValue(v: CatalogVariable): ReferenceOption | null {
  const sysId = typeof v.value === 'string' ? v.value.trim() : '';
  const display = v.displayValue?.trim() ?? '';
  if (!sysId || !display || sysId.startsWith('javascript:')) return null;
  const option: ReferenceOption = { sys_id: sysId, display_value: display };
  const columns: Record<string, string> = {};
  const userName = v.userName?.trim();
  const email = v.email?.trim();
  if (userName) columns['user_name'] = userName;
  if (email) columns['email'] = email;
  if (Object.keys(columns).length > 0) option.columns = columns;
  return option;
}

function _buildRenderVariable(
  v: CatalogVariable,
  meta: VarMeta | undefined,
  override: CatalogStaticOverride | undefined,
): RenderVariable {
  const flags = _mergeFlags(v, meta, override);
  const renderType = _resolveRenderType(v, meta);
  return {
    name: v.name,
    label: pickLabel(meta?.label, v.label, v.name),
    friendlyType: v.friendlyType,
    renderType,
    mandatory: flags.mandatory,
    readOnly: flags.readOnly,
    visible: flags.visible,
    defaultValue: _resolveDefaultValue(v, meta),
    helpText: _resolveHelpText(v, meta, renderType),
    maxLength: v.maxLength ?? meta?.maxLength ?? null,
    referenceTable: _resolveReferenceTable(v, meta, renderType),
    refQualifier: _resolveRefQualifier(v, meta),
    attributes: _mergeAttributes(v, meta),
    choices: _resolveChoices(v, meta, renderType),
    referenceValue: renderType === 'reference' ? _resolveReferenceValue(v) : null,
    html: _resolveRichTextHtml(v, meta),
    tooltip: meta?.tooltip ?? null,
    placeholder: meta?.exampleText ?? null,
    choiceDirection: meta?.choiceDirection === 'across' ? 'across' : 'down',
    useReferenceQualifier: meta?.useReferenceQualifier ?? null,
  };
}

// Item `friendlyType` is primary. Metadata reports SN dividers
// (numericType 24) as `type: "text"` with the variable name as label, so the
// numeric code overrides there (r2f 1.1.3 §12).
function _resolveRenderType(v: CatalogVariable, meta: VarMeta | undefined): RenderFieldType {
  if (meta?.numericType === '24') return 'split';
  // `||`, not `??`: an empty friendlyType ("") must fall back to metadata.
  return normaliseFieldType(v.friendlyType || meta?.type || meta?.numericType);
}

// Item help text wins; metadata `helpText` (r2f 1.1.3) otherwise. Metadata
// help text is skipped on checkboxes: on Galileo ~25 T&C checkboxes carry
// "Your Informatics product manager will need to approve this request.",
// which SN does not show there (r2f 1.1.3 §12 — confirm with form owners
// before enabling).
function _resolveHelpText(v: CatalogVariable, meta: VarMeta | undefined, renderType: RenderFieldType): string | null {
  if (v.helpText) return v.helpText;
  if (renderType === 'checkbox') return null;
  return meta?.helpText ?? null;
}

const YES_NO_CHOICES: CatalogChoice[] = [
  { value: '', label: '-- None --' },
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

function _isYesNo(v: CatalogVariable, meta: VarMeta | undefined): boolean {
  const raw = String(v.friendlyType ?? '').replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
  return raw === 'yes_no' || meta?.type === 'yesno' || meta?.numericType === '1';
}

// Item response ships the Yes/No choices; metadata does not. Fall back to
// SN's fixed set so the dropdown is never empty.
// Item choices first (SN's own API, includes `-- None --` when the item is
// configured for it). r2f 1.1.3 metadata ships `choices` for radio,
// multiselect and lookup selects too, but only the real ones (catrender
// v0.3.2) — so a dropdown fed from metadata gets the `-- None --`
// placeholder added here. Yes/No falls back to SN's fixed set.
const NONE_CHOICE: CatalogChoice = { value: '', label: '-- None --' };

function _resolveChoices(v: CatalogVariable, meta: VarMeta | undefined, renderType: RenderFieldType): CatalogChoice[] {
  if (v.choices?.length) return v.choices;
  if (meta?.choices?.length) {
    const needsNone = renderType === 'select' && !meta.choices.some((c) => c.value === '');
    return needsNone ? [NONE_CHOICE, ...meta.choices] : meta.choices;
  }
  return _isYesNo(v, meta) ? YES_NO_CHOICES : [];
}

// Rich text labels: metadata `annotationHtml` is authoritative; the item
// response carries the same markup in `label`.
function _resolveRichTextHtml(v: CatalogVariable, meta: VarMeta | undefined): string | null {
  if (meta?.annotationHtml) return cleanRichTextHtml(meta.annotationHtml);
  const raw = String(v.friendlyType ?? '').replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
  const isRichText = raw === 'rich_text_label' || meta?.type === 'annotation' || meta?.numericType === '32';
  return isRichText && v.label ? cleanRichTextHtml(v.label) : null;
}

const BLANK = /^[\s\u00a0]*$/;

function _isBlank(el: Element): boolean {
  return BLANK.test(el.textContent ?? '') && !el.querySelector('img, a, input, iframe');
}

// SN rich text lays notes out with tables whose look lives in inline styles
// (no borders, tinted background, an `&nbsp;` spacer column). Angular's
// sanitiser strips `style` but keeps `border="1"`, which leaves a bordered
// box with an empty first column. Drop the presentational leftovers so the
// component CSS can style the content: blank spacer cells, `border` /
// `cellpadding` / `cellspacing` attributes, `<colgroup>`, and blank leading /
// trailing paragraphs. Content is untouched; Angular still sanitises the result.
export function cleanRichTextHtml(html: string): string {
  if (!html || typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return html;
  root.querySelectorAll('colgroup').forEach((el) => el.remove());
  root.querySelectorAll('table').forEach((table) => {
    ['border', 'cellpadding', 'cellspacing', 'width', 'height'].forEach((a) => table.removeAttribute(a));
    table.querySelectorAll('tr').forEach((row) => {
      const cells = Array.from(row.children);
      // Keep at least one cell so a row never disappears.
      if (cells.some((c) => !_isBlank(c))) cells.filter(_isBlank).forEach((c) => c.remove());
    });
  });
  const edges = (pick: () => Element | null) => {
    for (let el = pick(); el && el.tagName === 'P' && _isBlank(el); el = pick()) el.remove();
  };
  edges(() => root.firstElementChild);
  edges(() => root.lastElementChild);
  return root.innerHTML;
}

export function mergeVariables(
  raw: CatalogVariable[],
  metadata: CatalogMetadata | null
): RenderVariable[] {
  const byName = new Map(
    (metadata?.variables ?? []).map((m) => [m.name, m] as const)
  );
  const overrides = collectStaticOverrides(metadata);
  // Inactive variables (r2f 1.1.3: `active: false`) are never rendered or
  // submitted, even where a UI policy says visible — drop them up front.
  const flat = flattenWrappers(raw).filter((v) => byName.get(v.name)?.active !== false);
  return flat.map((v) => _buildRenderVariable(v, byName.get(v.name), overrides.get(v.name)));
}

// Guide §5.6. Item response `attributes` is a comma-separated `key=value`
// blob. Values may themselves contain semicolon-separated lists (e.g.
// `ref_ac_columns=user_name;email`). We keep the raw value string here so
// consumers that care about the sub-list can split it themselves; this
// function's contract is purely `csv -> Record<string, string>`.
export function parseAttributes(csv: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!csv) return out;
  for (const pair of csv.split(',')) {
    const idx = pair.indexOf('=');
    if (idx <= 0) continue;
    const key = pair.slice(0, idx).trim();
    const value = pair.slice(idx + 1).trim();
    if (key) out[key] = value;
  }
  return out;
}

// Guide §4.1 / §9.2. Maps the wide zoo of SN `friendlyType` values (camelCase
// from Mulesoft, snake_case from raw SN, plus numeric fallbacks) onto the
// small stable render-type set the template switches on. Anything unknown
// falls through to 'text' — a safe read/write text input is always renderable.
//
// The guide's normaliser uses snake_case keys, but real Mulesoft returns
// camelCase (see the response the user pasted: selectBox, multiLineText).
// So the input is first canonicalised camelCase → snake_case before lookup.
const RENDER_TYPE_MAP: Record<string, RenderFieldType> = {
  single_line_text: 'text',
  wide_single_line_text: 'text',
  email: 'text',
  url: 'text',
  ip_address: 'text',
  ip: 'text',
  multi_line_text: 'textarea',
  html: 'textarea',
  // SN Multiple Choice renders as radio buttons on the portal (RoSE).
  multiple_choice: 'radio',
  radio: 'radio',
  select_box: 'select',
  lookup_select_box: 'select',
  check_box: 'checkbox',
  checkbox: 'checkbox',
  // SN Yes/No renders as a `-- None -- / Yes / No` dropdown and UI policies
  // compare against the literal 'Yes' / 'No' — a checkbox would hold
  // true/false and never match `app_using_dataiku=Yes`.
  yes_no: 'select',
  reference: 'reference',
  requested_for: 'reference',
  date: 'date',
  date_time: 'datetime',
  numeric_scale: 'integer',
  integer: 'integer',
  label: 'label',
  rich_text_label: 'label',
  // Metadata-side `type` aliases, used when the item omits friendlyType.
  annotation: 'label',
  yesno: 'select',
  container_start: 'section',
  container_end: 'section_end',
  container_split: 'split',
  break: 'divider',
  // r2f 1.1.3 metadata `type` vocabulary (used when the item omits
  // friendlyType). Identity entries so they count as known types.
  text: 'text',
  textarea: 'textarea',
  select: 'select',
  section: 'section',
  section_end: 'section_end',
  datetime: 'datetime',
  listcollector: 'reference_multi',
  multiselect: 'multiselect',
  multiple_select: 'multiselect',
  masked: 'masked',
  // Extra SN types the pilot catalog items surface that fell through to
  // 'text' before: HAR-sync gap analysis §D5 records the mapping decisions.
  // `boolean` — single flag; renders as a checkbox with the field label.
  // `choice` — SN's single-select dropdown; behaves like select_box.
  // `formatter` — decorative template stub; treat like a read-only label.
  // `checkbox_container` — groups related booleans; the surrounding section
  // header semantics are close enough without a bespoke render type.
  // `glide_list` — multi-value reference (chip list); dedicated render type
  // added for the Watchlist family of fields (§5b).
  boolean: 'checkbox',
  choice: 'select',
  // SN emits `formatter` for layout formatters (seen on Panorama Test Item 1
  // with the raw name as label) — skip it like other layout markers.
  formatter: 'split',
  checkbox_container: 'section',
  glide_list: 'reference_multi',
  // SN item-response alias for glide_list. Same chip-picker semantics.
  list_collector: 'reference_multi',
  // Numeric fallbacks the guide lists for records missing friendlyType.
  '1': 'select',
  '2': 'textarea',
  '3': 'radio',
  '4': 'integer',
  '5': 'select',
  '6': 'text',
  '7': 'checkbox',
  '8': 'reference',
  '9': 'date',
  '10': 'datetime',
  '11': 'label',
  '12': 'divider',
  '16': 'text',
  '19': 'section',
  '20': 'section_end',
  '21': 'reference_multi',
  '24': 'split',
  '25': 'masked',
  '32': 'label',
};

// True when `normaliseFieldType` has an explicit mapping for `raw`, i.e. it
// is not silently falling back to a text input.
export function isKnownFieldType(raw: NullableFriendlyType): boolean {
  if (raw == null || raw === '') return false;
  return RENDER_TYPE_MAP[_canonicalTypeKey(raw)] !== undefined || RENDER_TYPE_MAP[String(raw)] !== undefined;
}

function _canonicalTypeKey(raw: string | number): string {
  return String(raw)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // camelCase → snake_case
    .toLowerCase()
    .replace(/[\s\-/]+/g, '_');
}

// onChange target: an explicit name from the API, then `variableName` when it
// names a field (older payloads), then `IO:<sys_id>` mapped through metadata
// variable sys_ids. `null` means the script cannot be tied to a field — the
// caller must skip it rather than fire it on every change.
export function resolveOnChangeTarget(
  script: Pick<CatalogClientScript, 'appliesToVariableName' | 'watchedVariable' | 'variableName' | 'appliesToVariable'>,
  isField: (name: string) => boolean,
  metadata: CatalogMetadata | null,
): string | null {
  const named = [script.appliesToVariableName, script.watchedVariable, script.variableName].find(
    (n): n is string => !!n && isField(n),
  );
  if (named) return named;
  const sysId = script.appliesToVariable?.replace(/^IO:/, '');
  const bySysId = sysId ? metadata?.variables.find((v) => v.sysId === sysId)?.name : undefined;
  return bySysId && isField(bySysId) ? bySysId : null;
}

export function normaliseFieldType(
  raw: NullableFriendlyType
): RenderFieldType {
  if (raw == null || raw === '') return 'text';
  return RENDER_TYPE_MAP[_canonicalTypeKey(raw)] ?? RENDER_TYPE_MAP[String(raw)] ?? 'text';
}

// Guide §5.4. Walk in API-returned order (rule #1). A `section` opens a group,
// `section_end` closes it. Anything before the first section or after a close
// lands in an untitled section — that section is only created if needed, so
// forms with no containers still render as one flat group with title=null.
export function groupIntoSections(vars: RenderVariable[]): RenderSection[] {
  const sections: RenderSection[] = [];
  let current: RenderSection | null = null;
  for (const v of vars) {
    if (v.renderType === 'section') {
      current = { title: v.label, fields: [] };
      sections.push(current);
      continue;
    }
    if (v.renderType === 'section_end') {
      current = null;
      continue;
    }
    if (v.renderType === 'split') continue;
    if (!current) {
      const last = sections.at(-1);
      if (last?.title !== null) {
        current = { title: null, fields: [] };
        sections.push(current);
      } else {
        current = last;
      }
    }
    current.fields.push(v);
  }
  return sections;
}

function flattenWrappers(vars: CatalogVariable[]): CatalogVariable[] {
  const out: CatalogVariable[] = [];
  for (const v of vars) {
    if (v.order === -1 && Array.isArray(v.variables)) {
      out.push(...flattenWrappers(v.variables));
      continue;
    }
    out.push(v);
  }
  return out;
}

export function normaliseDefault(v: RenderVariable): unknown {
  switch (v.renderType) {
    case 'checkbox':
      return v.defaultValue === true || v.defaultValue === 'true';
    case 'integer': {
      if (v.defaultValue === null || v.defaultValue === '') return null;
      const n = Number(v.defaultValue);
      return Number.isFinite(n) ? n : null;
    }
    case 'reference':
      // Reference controls hold a `ReferenceOption` object (or null). Seed it
      // from the server-resolved selection when the item ships one; a bare
      // sys_id default has no label to show, so that still starts empty.
      return v.referenceValue ?? null;
    case 'reference_multi':
      // Multi-value reference (SN glide_list) — control holds an array of
      // ReferenceOption. Ships empty by default; users add chips as they
      // pick records from the typeahead.
      return [];
    default:
      return v.defaultValue == null ? '' : String(v.defaultValue);
  }
}
